import {ResignationReason, UpdateResignationReasonDto} from "@/models/resignation-reason.ts";
import {assertTokenValidForMutation, handleResponse} from "@/services/service-helpers.ts";
import log from '@/lib/logger';

const NBH_RESIGNATION_REASONS_BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/resignation-reasons`
const NBH_RESIGNATION_REASONS_ADMIN_BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/admin/resignation-reasons`

export async function fetchAllResignationReasons(token: string | null): Promise<ResignationReason[] | void> {
    if (!token) {
        log.error('No token provided. Can not fetch resignation reasons.');
        return Promise.reject("No token provided.")
    }

    const response = await fetch(`${NBH_RESIGNATION_REASONS_BASE_URL}`, {
        method: "GET",
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    })

    return handleResponse<ResignationReason[]>(response)
}

export async function createResignationReason(
    token: string | null,
    entry: ResignationReason
): Promise<ResignationReason | void> {
    if (!token) {
        log.error('No token provided. Can not create resignation reason.');
        return Promise.reject("No token provided.")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_RESIGNATION_REASONS_ADMIN_BASE_URL}`, {
        method: "POST",
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(entry)
    })

    return handleResponse<ResignationReason>(response)
}

export async function deleteResignationReason(token: string | null, resignationReasonId: string): Promise<void> {
    if (!token) {
        log.error('No token provided. Can not delete resignation reason.');
        return Promise.reject("No token provided.")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_RESIGNATION_REASONS_ADMIN_BASE_URL}/${resignationReasonId}`, {
        method: "DELETE",
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    })

    return handleResponse<void>(response)
}

export async function updateResignationReasonOrder(
    token: string | null,
    sortedIds: string[]
): Promise<void | ResignationReason[]> {
    if (!token) {
        log.error("No token provided. Can not update resignation reason order.")
        return Promise.reject("No token provided.")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_RESIGNATION_REASONS_ADMIN_BASE_URL}/sort-order`, {
        method: "PATCH",
        headers: new Headers({
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        }),
        body: JSON.stringify({ sortedIds })
    });

    return handleResponse<ResignationReason[]>(response);
}

export async function updateResignationReasonDetails(
    token: string | null,
    dto: UpdateResignationReasonDto
): Promise<void | ResignationReason> {
    if (!token) {
        log.error("No token provided. Can not update resignation reason.")
        return Promise.reject("No token provided.")
    }
    assertTokenValidForMutation()

    const {id, ...payload} = dto

    const response = await fetch(`${NBH_RESIGNATION_REASONS_ADMIN_BASE_URL}/${id}`, {
        method: "PATCH",
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    })

    return handleResponse<ResignationReason>(response)
}
